var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import { DEFAULT_CHAR_REGEX, awsPrefixCheck, invalidCharCheck, maxKeyLengthCheck, maxValueLengthCheck, emptyKeyCheck } from '../validation/rules';
var TagEditorController = /** @class */ (function () {
    function TagEditorController(component) {
        this.tags = [];
        this.__keyVisitCache = [];
        this.__component = component;
        this.__validCharRegex = DEFAULT_CHAR_REGEX;
        this.__valid = true;
        this.__keySuggestions = [];
    }
    TagEditorController.prototype.__notifyChange = function () {
        this.__component.__fireEvent('change', { tags: this.tags, valid: this.__valid });
    };
    TagEditorController.prototype.__validate = function () {
        var _this = this;
        var i18nStrings = this.__component.i18nStrings || {};
        // Create cache to use for duplicate key check
        var tagKeysCache = {};
        this.tags.forEach(function (tag) {
            if (!tag.markedForRemoval && tag.key) {
                tagKeysCache[tag.key] = tagKeysCache[tag.key] ? tagKeysCache[tag.key] + 1 : 1;
            }
        });
        var errors = this.tags.map(function () { return ({}); });
        this.tags.forEach(function (tag, index) {
            if (_this.__keyVisitCache[index] && emptyKeyCheck(tag.key)) {
                errors[index].key = i18nStrings.emptyKeyError;
            }
            else if (awsPrefixCheck(tag.key)) {
                errors[index].key = i18nStrings.awsPrefixError;
            }
            else if (invalidCharCheck(tag.key, _this.__validCharRegex)) {
                errors[index].key = i18nStrings.invalidKeyError;
            }
            else if (maxKeyLengthCheck(tag.key)) {
                errors[index].key = i18nStrings.maxKeyCharLengthError;
            }
            else if (tagKeysCache[tag.key] && tagKeysCache[tag.key] > 1) {
                errors[index].key = i18nStrings.duplicateKeyError;
            }
            if (invalidCharCheck(tag.value, _this.__validCharRegex)) {
                errors[index].value = i18nStrings.invalidValueError;
            }
            else if (maxValueLengthCheck(tag.value)) {
                errors[index].value = i18nStrings.maxValueCharLengthError;
            }
            if (tag.markedForRemoval) {
                delete errors[index].value;
            }
        });
        if (JSON.stringify(errors) !== JSON.stringify(this.__component.__errors)) {
            this.__valid = errors.reduce(function (acc, error) { return !error.key && !error.value && acc; }, true);
            this.__component.__errors = __spreadArrays(errors);
        }
        return this.__valid;
    };
    TagEditorController.prototype.__validateAndNotify = function () {
        var oldVal = this.__valid;
        var newVal = this.__validate();
        if (oldVal !== newVal) {
            this.__notifyChange();
        }
    };
    TagEditorController.prototype.tagsPropertyChangeHandler = function (tags) {
        this.tags = tags;
        this.__validateAndNotify();
    };
    TagEditorController.prototype.allowedCharacterPatternPropertyChangeHandler = function (allowedCharacterPattern) {
        // This is an edge case specifically to cater for EC2 who has a super set of allowed characters
        // This replaces the default validation rule set by swapping out the character pattern rules
        this.__validCharRegex = new RegExp(allowedCharacterPattern);
        this.__validateAndNotify();
    };
    TagEditorController.prototype.getAvailableTagsCount = function () {
        return this.__component.tagLimit - this.tags.filter(function (tag) { return !tag.markedForRemoval; }).length;
    };
    TagEditorController.prototype.getAdditionalInfoText = function () {
        var i18nStrings = this.__component.i18nStrings || {};
        var defaultFn = function () { return ''; };
        if (this.isTagLimitReached()) {
            return (i18nStrings.tagLimitReached || defaultFn)(this.__component.tagLimit);
        }
        else if (this.isTagLimitExceeded()) {
            return (i18nStrings.tagLimitExceeded || defaultFn)(this.__component.tagLimit);
        }
        else {
            return (i18nStrings.tagLimit || defaultFn)(this.getAvailableTagsCount());
        }
    };
    TagEditorController.prototype.getFirstError = function () {
        for (var index = 0; index < this.__component.__errors.length; index++) {
            if (this.__component.__errors[index].key || this.__component.__errors[index].value) {
                return __assign({ index: index }, this.__component.__errors[index]);
            }
        }
        return null;
    };
    TagEditorController.prototype.isAddButtonDisabled = function () {
        return this.isTagLimitReached() || this.isTagLimitExceeded();
    };
    TagEditorController.prototype.isTagLimitReached = function () {
        return this.getAvailableTagsCount() === 0;
    };
    TagEditorController.prototype.isTagLimitExceeded = function () {
        return this.getAvailableTagsCount() < 0;
    };
    TagEditorController.prototype.isTagRemovable = function (item) {
        return !item.markedForRemoval;
    };
    TagEditorController.prototype.addTag = function () {
        var newTag = { key: '', value: '', existing: false, markedForRemoval: false, valid: true };
        this.tags = __spreadArrays(this.tags, [newTag]);
        this.__component.__errors = __spreadArrays(this.__component.__errors, [{}]);
        // Validation not required here as a new tag should not be in an invalid state and so the component valid state will not change
        this.__notifyChange();
    };
    TagEditorController.prototype.removeTag = function (itemIndex) {
        if (this.tags[itemIndex].existing) {
            var tag = this.tags[itemIndex];
            tag.markedForRemoval = true;
            this.tags = __spreadArrays(this.tags.slice(0, itemIndex), [tag], this.tags.slice(itemIndex + 1));
        }
        else {
            this.tags = __spreadArrays(this.tags.slice(0, itemIndex), this.tags.slice(itemIndex + 1));
            this.__keyVisitCache = __spreadArrays(this.__keyVisitCache.slice(0, itemIndex), this.__keyVisitCache.slice(itemIndex + 1));
        }
        this.__validate();
        this.__notifyChange();
    };
    TagEditorController.prototype.undoTagRemoval = function (itemIndex) {
        var tag = this.tags[itemIndex];
        tag.markedForRemoval = false; // GWT
        delete tag.markedForRemoval;
        this.tags = __spreadArrays(this.tags.slice(0, itemIndex), [tag], this.tags.slice(itemIndex + 1));
        this.__validate();
        this.__notifyChange();
    };
    // Interaction handling
    TagEditorController.prototype.keyInputHandler = function (newKey, itemIndex) {
        this.tags = this.tags.map(function (tag, index) { return (index === itemIndex ? __assign(__assign({}, tag), { key: newKey }) : tag); });
        this.__validateAndNotify();
    };
    TagEditorController.prototype.valueInputHandler = function (value, itemIndex) {
        this.tags = this.tags.map(function (tag, index) { return (index === itemIndex ? __assign(__assign({}, tag), { value: value }) : tag); });
        this.__validateAndNotify();
    };
    TagEditorController.prototype.keyBlurHandler = function (itemIndex) {
        this.__keyVisitCache[itemIndex] = true;
        this.__validateAndNotify();
    };
    TagEditorController.prototype.doKeyRequest = function (key) {
        var _this = this;
        return this.__component.keysRequest(key).then(function (tagKeys) {
            var result = tagKeys.map(function (value) { return ({ value: value }); });
            if (key === '') {
                _this.__keySuggestions = result;
            }
            return result;
        });
    };
    TagEditorController.prototype.doValueRequest = function (key, value) {
        return this.__component.valuesRequest(key, value).then(function (tagValues) { return tagValues.map(function (value) { return ({ value: value }); }); });
    };
    return TagEditorController;
}());
export default TagEditorController;
//# sourceMappingURL=tag-editor-controller.js.map